import { CreateFocusCss, isTouchScreen } from '../../../styles'
import type { Tokens } from '../../../theming/types'
import type { ButtonKind, ButtonProps } from '..'

export type KindStyles = {
  [key in Exclude<ButtonKind, 'brand'>]: {
    color: {
      backgroundColor: string
      borderColor?: string
      color: string
    }
    icon?: {
      fill: string
    }
    hover: {
      backgroundColor: string
      borderColor?: string
      color?: string
    }
    active?: {
      backgroundColor: string
      borderColor?: string
    }
    focus?: {
      outlineColor: string
    }
    disabled?: {
      color?: string
      backgroundColor: string
    }
    seperator?: {
      borderInlineStartColor: string
    }
  }
}

export default (config: {
  tokens: Tokens
  props: Pick<
    ButtonProps,
    'animate' | 'inverted' | 'kind' | 'loading' | 'round' | 'size'
  >
}) => {
  const { tokens, props } = config
  const kind = props.kind === 'brand' ? 'main' : props.kind!
  const btnTokens = props.inverted ? tokens.dark : tokens

  const kindStyles: KindStyles = {
    main: {
      color: {
        backgroundColor: btnTokens.ctaBgColorBrandDefault,
        color: btnTokens.fontColorInverse,
      },
      hover: {
        backgroundColor: btnTokens.ctaBgColorBrandHover,
      },
      active: {
        backgroundColor: btnTokens.ctaBgColorBrandActive,
      },
      seperator: {
        borderInlineStartColor: btnTokens.ctaComboBorderColorBrandDefault,
      },
      disabled: {
        backgroundColor: btnTokens.ctaBgColorBrandDefault,
      },
    },
    primary: {
      color: {
        backgroundColor: btnTokens.ctaBgColorPrimaryDefault,
        color: btnTokens.fontColorInverse,
      },
      hover: {
        backgroundColor: btnTokens.ctaBgColorPrimaryHover,
        color: btnTokens.fontColorInverse,
      },
      active: {
        backgroundColor: btnTokens.ctaBgColorPrimaryActive,
      },
      seperator: {
        borderInlineStartColor: btnTokens.ctaComboBorderColorPrimaryDefault,
      },
      disabled: {
        backgroundColor: btnTokens.ctaBgColorPrimaryDefault,
      },
    },
    secondary: {
      color: {
        backgroundColor: props.round
          ? btnTokens.bgColorPopoverClose
          : btnTokens.ctaBgColorSecondaryDefault,
        borderColor: btnTokens.ctaBorderColorSecondaryDefault,
        color: btnTokens.fontColorDefault,
      },
      hover: {
        backgroundColor: btnTokens.ctaBgColorSecondaryHover,
        borderColor: btnTokens.ctaBorderColorSecondaryHover,
        color: btnTokens.fontColorDefault,
      },
      active: {
        backgroundColor: btnTokens.ctaBgColorSecondaryActive,
        borderColor: btnTokens.ctaBorderColorSecondaryActive,
      },
      seperator: {
        borderInlineStartColor: btnTokens.ctaComboBorderColorSecondaryDefault,
      },
      disabled: {
        backgroundColor: props.round
          ? btnTokens.bgColorPopoverClose
          : btnTokens.ctaBgColorSecondaryDefault,
      },
      icon: {
        fill: btnTokens.iconColorDefault,
      },
    },
    tertiary: {
      color: {
        backgroundColor: props.round
          ? btnTokens.bgColorPopoverClose
          : btnTokens.ctaBgColorTertiaryDefault,
        color: btnTokens.fontColorDefault,
      },
      hover: {
        backgroundColor: btnTokens.ctaBgColorTertiaryHover,
        color: btnTokens.fontColorDefault,
      },
      active: {
        backgroundColor: btnTokens.ctaBgColorTertiaryActive,
      },
      seperator: {
        borderInlineStartColor: 'transparent',
      },
      disabled: {
        backgroundColor: props.round
          ? btnTokens.bgColorPopoverClose
          : btnTokens.ctaBgColorTertiaryDefault,
      },
      icon: {
        fill: btnTokens.iconColorDefault,
      },
    },
    danger: {
      color: {
        backgroundColor: btnTokens.ctaBgColorDangerDefault,
        color: btnTokens.fontColorInverse,
      },
      hover: {
        backgroundColor: btnTokens.ctaBgColorDangerHover,
        color: btnTokens.fontColorInverse,
      },
      active: {
        backgroundColor: btnTokens.ctaBgColorDangerActive,
      },
      seperator: {
        borderInlineStartColor: btnTokens.ctaComboBorderColorPrimaryDefault,
      },
      disabled: {
        backgroundColor: btnTokens.ctaBgColorDangerDefault,
      },
    },
  }

  return {
    button: {
      default: {
        borderRadius: tokens.radiusSizeXS,
        letterSpacing: 'normal',
        minHeight: props.size === 'small' ? tokens.ctaSizeSm : tokens.ctaSizeMd,
        minWidth: props.animate ? undefined : '80px',
        paddingBlock: tokens.spacingGap50,
        // eslint-disable-next-line no-nested-ternary
        paddingInline: props.animate
          ? '8px'
          : props.size === 'small'
            ? tokens.spacingGap100
            : tokens.spacingGap150,
        textTransform: 'none',
        ...kindStyles[kind].color,

        ...CreateFocusCss(btnTokens.focusOuter),

        // Secondary and Tertiary buttons have a different color for the start/endElement icons than the text color. Defaults to currentColor in base.ts.
        ...(kindStyles[kind].icon && {
          '& svg': { ...kindStyles[kind].icon },
        }),

        '&:hover': { ...kindStyles[kind].hover },
        '&:active, &[aria-expanded="true"]': { ...kindStyles[kind].active },
        '&:disabled': {
          opacity: tokens.opacityDisabled,
          cursor: 'not-allowed',
          ...kindStyles[kind].disabled,
        },

        [isTouchScreen]: {
          height: tokens.ctaSizeLg,
          minHeight: tokens.ctaSizeLg,
        },
      },

      round: {
        height: tokens.ctaSizeMd,
        minHeight: tokens.ctaSizeMd,
        width: tokens.ctaSizeMd,
      },

      active: {
        ...kindStyles[kind].active,
      },

      // These styles are only used in FileInput Component
      disabled: {
        '&:hover': kindStyles[kind].disabled,
      },

      // These styles are only used in ButtonGroup and the ComboButton components
      buttonSeparatorColor: kindStyles[kind].seperator,
    },

    menuTrigger: {
      paddingInlineEnd:
        props.size === 'small' ? tokens.spacingGap100 : tokens.spacingGap150,
    },

    caret: {
      marginInlineStart: tokens.spacingGap100,
    },

    hideText: {
      minWidth: props.size === 'small' ? tokens.ctaSizeSm : tokens.ctaSizeMd,
      paddingBlock: props.loading ? '7px' : undefined,
      paddingInline: props.loading ? '7px' : undefined,

      [isTouchScreen]: {
        minWidth: tokens.ctaSizeLg,
        paddingInline: '3px',
      },
    },

    icon: {
      beforeText: {
        marginInlineEnd:
          props.size === 'small' ? tokens.spacingGap50 : tokens.spacingGap100,
      },

      afterText: {
        marginInlineStart:
          props.size === 'small' ? tokens.spacingGap50 : tokens.spacingGap100,
      },

      hideText: {
        padding: props.size === 'small' ? '5px' : '7px',
      },
    },

    iconButton: {
      default: {
        paddingBlock: '7px',
        paddingInline: '15px',

        [isTouchScreen]: {
          paddingBlock: '11px',
        },
      },

      beforeText: {
        paddingInlineStart: '11px',

        [isTouchScreen]: {
          paddingInlineStart: '15px',
        },
      },

      afterText: {
        paddingInlineEnd: '11px',

        [isTouchScreen]: {
          paddingInlineEnd: '15px',
        },
      },
    },

    pill: {
      borderRadius: tokens.radiusSizeFull,
    },
  } as const
}
